import { useState, useEffect, useMemo } from 'react';
import { Modal, Table, Button, Spinner, Alert, Tab, Nav, Badge } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { Company } from '../../../../interfaces/Company';
import { InsurancePolicy, InsurancePolicyMainBranch } from '../../../../interfaces/InsurancePolicy';
import { InsuranceDiscount, DiscountYear } from '../../../../interfaces/InsuranceDiscount';
import { getInsuranceDiscounts } from '../../../../utils/api/insuranceDiscounts';
import { calculatePremiumChangeTable } from '../../../../utils/api/insurancePolicy';
import { getCoverageTypes } from '../../../../utils/api/insurancePricing';

interface CompanyComparisonModalProps {
    show: boolean;
    onHide: () => void;
    companies: Company[];
    policy: InsurancePolicy;
    onSelectCompanyDiscount: (companyId: string, discountId: string | undefined) => void;
}

interface YearlyPremium {
    year: number;
    age: number;
    premium: number;
}

interface PersonPremiumData {
    name: string;
    idNumber: string;
    currentAge: number;
    yearlyPremiums: YearlyPremium[];
}

interface CompanyPremiumData {
    companyId: string;
    companyName: string;
    discounts: InsuranceDiscount[];
    selectedDiscountId: string | undefined;
    persons: PersonPremiumData[];
    loading: boolean;
    error: string | null;
}

// Group discount years into ranges for display
const groupDiscountYears = (years: DiscountYear[]): { range: string; percentage: number }[] => {
    if (!years || years.length === 0) return [];
    const sorted = [...years].sort((a, b) => a.year - b.year);
    const groups: { start: number; end: number; percentage: number }[] = [];

    for (const year of sorted) {
        const lastGroup = groups[groups.length - 1];
        if (lastGroup && year.year === lastGroup.end + 1 && year.percentage === lastGroup.percentage) {
            lastGroup.end = year.year;
        } else {
            groups.push({ start: year.year, end: year.year, percentage: year.percentage });
        }
    }

    return groups.map((g) => ({
        range: g.start === g.end ? g.start.toString() : `${g.start}-${g.end}`,
        percentage: g.percentage,
    }));
};

// Calculate age from date of birth
const calculateAge = (dateOfBirth: Date | string | undefined): number | undefined => {
    if (!dateOfBirth) return undefined;
    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }
    return age;
};

const CompanyComparisonModal = ({ show, onHide, companies, policy, onSelectCompanyDiscount }: CompanyComparisonModalProps) => {
    const { t } = useTranslation('insurance');
    const [companyData, setCompanyData] = useState<Map<string, CompanyPremiumData>>(new Map());
    const [loading, setLoading] = useState(false);
    const [selectedPersonTab, setSelectedPersonTab] = useState<string>('all');
    const [maxAge, setMaxAge] = useState<number>(75);
    const [validCoverageTypes, setValidCoverageTypes] = useState<Set<string>>(new Set());

    // Get the insured persons from the policy, enriched with data from insuredPersonCoverages
    const insuredPersons = useMemo(() => {
        const baseList = policy.insuredList || [];
        // Enrich with data from insuredPersonCoverages if available
        return baseList.map((person) => {
            const coverageEntry = policy.insuredPersonCoverages?.find(
                (ipc) => ipc.insuredPerson.idNumber === person.idNumber
            );
            if (coverageEntry) {
                return {
                    ...person,
                    // Use dateOfBirth from coverageEntry if not set on person
                    dateOfBirth: person.dateOfBirth || coverageEntry.insuredPerson.dateOfBirth,
                    // Use gender from coverageEntry if not set on person
                    gender: person.gender || coverageEntry.insuredPerson.gender,
                    // Use smokerStatus from coverageEntry if not set on person
                    smokerStatus: person.smokerStatus || coverageEntry.smokerStatus,
                };
            }
            return person;
        });
    }, [policy.insuredList, policy.insuredPersonCoverages]);
    const branch = policy.branch;
    const totalCoverage = policy.totalCoverage || 0;

    // Calculate the oldest person's age for reference
    const oldestAge = useMemo(() => {
        const ages = insuredPersons
            .map((p) => calculateAge(p.dateOfBirth))
            .filter((age): age is number => age !== undefined);
        return ages.length > 0 ? Math.max(...ages) : 30;
    }, [insuredPersons]);

    // Calculate the current (starting) age for the dropdown
    const currentAge = useMemo(() => {
        const ages = insuredPersons
            .map((p) => calculateAge(p.dateOfBirth))
            .filter((age): age is number => age !== undefined);
        return ages.length > 0 ? Math.max(...ages) : 30;
    }, [insuredPersons]);

    // Fetch valid coverage types for the branch when modal opens
    useEffect(() => {
        if (!show || !branch || companies.length === 0) return;

        const fetchValidCoverageTypes = async () => {
            try {
                // Fetch coverage types from the first company to determine valid types for this branch
                const coverageTypesResponse = await getCoverageTypes({
                    branch: branch,
                    company: companies[0]._id,
                });
                const coverageTypes = coverageTypesResponse.coverageTypes || [];
                setValidCoverageTypes(new Set(coverageTypes));
            } catch (error) {
                console.error('Error fetching coverage types:', error);
                // If we can't fetch, don't filter (show all)
                setValidCoverageTypes(new Set());
            }
        };

        fetchValidCoverageTypes();
    }, [show, branch, companies]);

    // Fetch discounts for all companies when modal opens (after valid coverage types are fetched)
    useEffect(() => {
        if (!show || companies.length === 0 || !branch || insuredPersons.length === 0) return;
        // Wait a bit for validCoverageTypes to be fetched (it's fetched in parallel)
        // If validCoverageTypes is still empty after a short delay, proceed anyway

        const fetchDiscountsForAllCompanies = async () => {
            setLoading(true);
            setSelectedPersonTab('all');
            const newCompanyData = new Map<string, CompanyPremiumData>();

            // Initialize company data
            for (const company of companies) {
                newCompanyData.set(company._id, {
                    companyId: company._id,
                    companyName: company.shortName || company.name,
                    discounts: [],
                    selectedDiscountId: undefined,
                    persons: [],
                    loading: true,
                    error: null,
                });
            }
            setCompanyData(new Map(newCompanyData));

            // Fetch discounts for each company in parallel
            await Promise.all(
                companies.map(async (company) => {
                    try {
                        const discountParams: any = {
                            company: company._id,
                            branch: branch,
                            limit: 100,
                        };

                        if (totalCoverage && totalCoverage > 0) {
                            discountParams.coverage = totalCoverage;
                        }

                        if (oldestAge) {
                            discountParams.age = oldestAge;
                        }

                        const discountResponse = await getInsuranceDiscounts(discountParams);
                        const discounts: InsuranceDiscount[] = discountResponse.discounts || [];

                        // Update company data with discounts
                        setCompanyData((prev) => {
                            const newMap = new Map(prev);
                            const existing = newMap.get(company._id);
                            if (existing) {
                                existing.discounts = discounts;
                                existing.selectedDiscountId = discounts.length > 0 ? discounts[0]._id : undefined;
                            }
                            return new Map(newMap);
                        });

                        // Fetch premium data for the first discount (or no discount)
                        await fetchPremiumDataForCompany(company._id, discounts.length > 0 ? discounts[0]._id : undefined);
                    } catch (error: any) {
                        console.error(`Error fetching discounts for company ${company.shortName}:`, error);
                        setCompanyData((prev) => {
                            const newMap = new Map(prev);
                            const existing = newMap.get(company._id);
                            if (existing) {
                                existing.loading = false;
                                existing.error = error.message || 'Error loading data';
                            }
                            return new Map(newMap);
                        });
                    }
                })
            );

            setLoading(false);
        };

        fetchDiscountsForAllCompanies();
    }, [show, companies, branch, totalCoverage, oldestAge, insuredPersons.length, validCoverageTypes]);

    // Fetch premium data for a specific company/discount combination
    const fetchPremiumDataForCompany = async (companyId: string, discountId: string | undefined) => {
        setCompanyData((prev) => {
            const newMap = new Map(prev);
            const existing = newMap.get(companyId);
            if (existing) {
                existing.loading = true;
                existing.error = null;
            }
            return new Map(newMap);
        });

        try {
            const params = {
                company: companyId,
                branch: branch,
                totalCoverage: totalCoverage,
                insuranceDiscount: discountId,
                insuredList: insuredPersons.map((p) => ({
                    idNumber: p.idNumber,
                    name: p.name,
                    dateOfBirth: p.dateOfBirth,
                    gender: p.gender,
                    smokerStatus: p.smokerStatus || 'nonSmoker',
                    occupationMultiplier: p.occupationMultiplier,
                })),
                insuredPersonCoverages: policy.insuredPersonCoverages
                    ?.map((ipc) => ({
                        insuredPerson: { idNumber: ipc.insuredPerson.idNumber },
                        selectedCoverages: ipc.selectedCoverages
                            .map((sc) => ({
                                coverageType: typeof sc === 'object' ? sc.coverageType : '',
                                insurancePricingId: typeof sc === 'object' ? sc.insurancePricingId : sc,
                            }))
                            .filter((sc) => {
                                // Filter to only include coverage types valid for this branch
                                if (!sc.coverageType) return false;
                                // If we have valid coverage types, filter by them
                                if (validCoverageTypes.size > 0) {
                                    return validCoverageTypes.has(sc.coverageType);
                                }
                                // If we don't have valid types yet, include all (will be filtered on next call)
                                return true;
                            }),
                    }))
                    .filter((ipc) => ipc.selectedCoverages.length > 0), // Only include persons with valid coverages
                mortgageDetails: policy.mortgageDetails,
            };

            console.log('CompanyComparisonModal - API params:', JSON.stringify(params, null, 2));
            console.log('CompanyComparisonModal - policy.insuredList:', JSON.stringify(policy.insuredList, null, 2));
            console.log('CompanyComparisonModal - policy.insuredPersonCoverages:', JSON.stringify(policy.insuredPersonCoverages, null, 2));
            console.log('CompanyComparisonModal - enriched insuredPersons:', JSON.stringify(insuredPersons, null, 2));

            const response = await calculatePremiumChangeTable(params);

            // Store per-person data from the API response
            const persons: PersonPremiumData[] = response.persons || [];

            // Update valid coverage types if we got data back (in case initial fetch failed)
            if (persons.length > 0 && validCoverageTypes.size === 0) {
                // Extract coverage types from the response if available
                // This is a fallback if the initial coverage types fetch failed
            }

            setCompanyData((prev) => {
                const newMap = new Map(prev);
                const existing = newMap.get(companyId);
                if (existing) {
                    existing.persons = persons;
                    existing.loading = false;
                    existing.error = null;
                }
                return new Map(newMap);
            });
        } catch (error: any) {
            console.error(`Error fetching premium data for company ${companyId}:`, error);
            setCompanyData((prev) => {
                const newMap = new Map(prev);
                const existing = newMap.get(companyId);
                if (existing) {
                    existing.loading = false;
                    existing.error = error.message || 'Error loading premium data';
                }
                return new Map(newMap);
            });
        }
    };

    // Handle discount selection change for a company
    const handleDiscountChange = async (companyId: string, discountId: string | undefined) => {
        setCompanyData((prev) => {
            const newMap = new Map(prev);
            const existing = newMap.get(companyId);
            if (existing) {
                existing.selectedDiscountId = discountId;
            }
            return new Map(newMap);
        });

        // Fetch new premium data for the selected discount
        await fetchPremiumDataForCompany(companyId, discountId);
    };

    // Handle selection of a company/discount combination
    const handleSelectCombination = (companyId: string, discountId: string | undefined) => {
        onSelectCompanyDiscount(companyId, discountId);
        onHide();
    };

    // Get all unique years from all companies for the table rows
    // Also generate years based on insured persons' ages to ensure rows appear even when a company has no pricing
    const allYears = useMemo(() => {
        const yearsSet = new Set<number>();
        const currentYear = new Date().getFullYear();

        // First, collect years from API responses
        companyData.forEach((data) => {
            data.persons.forEach((person) => {
                person.yearlyPremiums.forEach((yp) => yearsSet.add(yp.year));
            });
        });

        // If no years from API, generate years based on insured persons' ages (up to age 80)
        if (yearsSet.size === 0 && insuredPersons.length > 0) {
            const ages = insuredPersons
                .map((p) => calculateAge(p.dateOfBirth))
                .filter((age): age is number => age !== undefined && age < 80);
            if (ages.length > 0) {
                const youngestAge = Math.min(...ages);
                const yearsUntil80 = 80 - youngestAge;
                for (let i = 0; i < Math.min(yearsUntil80, 50); i++) {
                    yearsSet.add(currentYear + i);
                }
            }
        }

        return Array.from(yearsSet).sort((a, b) => a - b);
    }, [companyData, insuredPersons]);

    // Get all unique person idNumbers from company data or fall back to insuredPersons
    const allPersons = useMemo(() => {
        // First try to get persons from company data
        const dataArray = Array.from(companyData.values());
        for (const data of dataArray) {
            if (data.persons.length > 0) {
                return data.persons.map((p) => ({ idNumber: p.idNumber, name: p.name }));
            }
        }
        // Fall back to insuredPersons from the policy
        if (insuredPersons.length > 0) {
            return insuredPersons.map((p) => ({ idNumber: p.idNumber, name: p.name }));
        }
        return [];
    }, [companyData, insuredPersons]);

    // Get premium for a specific company, year, and optionally person
    const getPremiumForYear = (
        companyId: string,
        year: number,
        personIdNumber?: string
    ): { premium: number; age: number } | null => {
        const data = companyData.get(companyId);
        if (!data || data.persons.length === 0) return null;

        if (personIdNumber) {
            // Get premium for specific person
            const person = data.persons.find((p) => p.idNumber === personIdNumber);
            if (!person) return null;
            const yearData = person.yearlyPremiums.find((yp) => yp.year === year);
            return yearData ? { premium: yearData.premium, age: yearData.age } : null;
        } else {
            // Get total premium for all persons
            let totalPremium = 0;
            let age: number | null = null;
            data.persons.forEach((person) => {
                const yearData = person.yearlyPremiums.find((yp) => yp.year === year);
                if (yearData) {
                    totalPremium += yearData.premium;
                    if (age === null) age = yearData.age;
                }
            });
            return totalPremium > 0 ? { premium: totalPremium, age: age || 0 } : null;
        }
    };

    // Find the minimum premium for each year to highlight
    const getMinPremiumCompany = (year: number, personIdNumber?: string): string | null => {
        let minPremium = Infinity;
        let minCompanyId: string | null = null;

        companies.forEach((company) => {
            const data = companyData.get(company._id);
            if (!data || data.loading || data.error) return;

            const premiumData = getPremiumForYear(company._id, year, personIdNumber);
            if (premiumData && premiumData.premium > 0 && premiumData.premium < minPremium) {
                minPremium = premiumData.premium;
                minCompanyId = company._id;
            }
        });

        return minCompanyId;
    };

    const isAnyLoading = Array.from(companyData.values()).some((d) => d.loading);

    // Get all unique coverage types from the policy's insuredPersonCoverages, filtered by valid types for the branch
    const selectedCoverageTypes = useMemo(() => {
        const coverageTypes = new Set<string>();
        policy.insuredPersonCoverages?.forEach((ipc) => {
            ipc.selectedCoverages?.forEach((sc) => {
                if (typeof sc === 'object' && sc.coverageType) {
                    // Only include coverage types that are valid for this branch
                    if (validCoverageTypes.size === 0 || validCoverageTypes.has(sc.coverageType)) {
                        coverageTypes.add(sc.coverageType);
                    }
                }
            });
        });
        return Array.from(coverageTypes);
    }, [policy.insuredPersonCoverages, validCoverageTypes]);

    // Get the currently selected person idNumber (or undefined for "all")
    const selectedPersonIdNumber = selectedPersonTab === 'all' ? undefined : selectedPersonTab;

    // Check if we're showing all insured (no specific person selected) AND have multiple persons
    const isAllInsured = selectedPersonTab === 'all';
    const hasMultiplePersons = allPersons.length > 1;
    // Show age column when: a specific person is selected OR there's only one insured person
    const showAgeColumn = !isAllInsured || !hasMultiplePersons;

    // Render the comparison table
    const renderComparisonTable = () => (
        <Table striped bordered hover responsive size="sm">
            <thead className="table-primary">
                <tr>
                    <th style={{ minWidth: '60px' }}>{t('Year')}</th>
                    {showAgeColumn && <th style={{ minWidth: '60px' }}>{t('Age')}</th>}
                    {companies.map((company) => {
                        const data = companyData.get(company._id);
                        return (
                            <th key={company._id} style={{ minWidth: '150px' }}>
                                <div className="d-flex flex-column gap-2">
                                    <div className="d-flex align-items-center justify-content-between gap-2">
                                        {company.thumbUrl ? (
                                            <div style={{ height: '39px', display: 'flex', alignItems: 'center' }}>
                                                <img src={company.thumbUrl} alt={company.shortName || company.name} style={{ maxHeight: '78px', maxWidth: '195px' }} />
                                            </div>
                                        ) : (
                                            <span className="fw-bold">{company.shortName || company.name}</span>
                                        )}
                                        <Button
                                            variant="outline-success"
                                            size="sm"
                                            onClick={() => handleSelectCombination(company._id, data?.selectedDiscountId)}
                                            disabled={data?.loading || !!data?.error}
                                        >
                                            {t('Select')}
                                        </Button>
                                    </div>
                                    {data && data.discounts.length > 0 && (
                                        <Select
                                            options={data.discounts.map((d) => {
                                                const yearsStr = d.discountYears
                                                    ? groupDiscountYears(d.discountYears)
                                                          .map((g) => `${g.range}: ${g.percentage}%`)
                                                          .join(', ')
                                                    : '';
                                                return {
                                                    value: d._id,
                                                    label: `${d.description} (${yearsStr})`,
                                                };
                                            })}
                                            value={
                                                data.selectedDiscountId
                                                    ? (() => {
                                                          const d = data.discounts.find(
                                                              (disc) => disc._id === data.selectedDiscountId
                                                          );
                                                          if (!d) return null;
                                                          const yearsStr = d.discountYears
                                                              ? groupDiscountYears(d.discountYears)
                                                                    .map((g) => `${g.range}: ${g.percentage}%`)
                                                                    .join(', ')
                                                              : '';
                                                          return {
                                                              value: d._id,
                                                              label: `${d.description} (${yearsStr})`,
                                                          };
                                                      })()
                                                    : null
                                            }
                                            onChange={(option) => handleDiscountChange(company._id, option?.value)}
                                            isClearable
                                            placeholder={t('Select Discount...')}
                                            menuPortalTarget={document.body}
                                            styles={{
                                                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                                control: (base) => ({ ...base, minHeight: '30px', fontSize: '0.8rem' }),
                                                valueContainer: (base) => ({ ...base, padding: '0 4px' }),
                                                input: (base) => ({ ...base, margin: 0, padding: 0 }),
                                                indicatorsContainer: (base) => ({ ...base, height: '28px' }),
                                            }}
                                        />
                                    )}
                                    {data && data.discounts.length === 0 && !data.loading && (
                                        <span className="text-muted small">{t('No discounts')}</span>
                                    )}
                                </div>
                            </th>
                        );
                    })}
                </tr>
            </thead>
            <tbody>
                {/* Total Premium Row */}
                <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                    <td>
                        <strong>{t('Total')}</strong>
                    </td>
                    {showAgeColumn && (
                        <td>
                            <select
                                className="form-select form-select-sm"
                                style={{ width: 'auto', minWidth: '60px' }}
                                value={maxAge}
                                onChange={(e) => setMaxAge(Number(e.target.value))}
                            >
                                {Array.from({ length: 80 - currentAge }, (_, i) => currentAge + 1 + i).map((age) => (
                                    <option key={age} value={age}>
                                        {age}
                                    </option>
                                ))}
                            </select>
                        </td>
                    )}
                    {companies.map((company) => {
                        const data = companyData.get(company._id);
                        if (data?.loading) {
                            return (
                                <td key={company._id}>
                                    <Spinner animation="border" size="sm" />
                                </td>
                            );
                        }
                        if (data?.error) {
                            return (
                                <td key={company._id}>
                                    <span className="text-danger">שגיאה</span>
                                </td>
                            );
                        }

                        // Calculate total premium for all years up to maxAge
                        let totalPremium = 0;
                        let hasMissingData = false;

                        for (const year of allYears) {
                            // Get age for this year
                            let yearAge: number | null = null;
                            for (const comp of companies) {
                                const pd = getPremiumForYear(comp._id, year, selectedPersonIdNumber);
                                if (pd) {
                                    yearAge = pd.age;
                                    break;
                                }
                            }

                            // Skip years beyond maxAge
                            if (yearAge !== null && yearAge > maxAge) {
                                continue;
                            }

                            const premiumData = getPremiumForYear(company._id, year, selectedPersonIdNumber);
                            if (premiumData && premiumData.premium > 0) {
                                totalPremium += 12 * premiumData.premium;
                            } else if (yearAge !== null && yearAge <= maxAge) {
                                // Missing data for a year that should be included
                                hasMissingData = true;
                            }
                        }

                        return (
                            <td key={company._id}>
                                {hasMissingData ? t('No data') : `₪${totalPremium.toFixed(0)}`}
                            </td>
                        );
                    })}
                </tr>
                {allYears.map((year) => {
                    const minCompanyId = getMinPremiumCompany(year, selectedPersonIdNumber);
                    // Get the age from the first company that has data for this year
                    let displayAge: number | null = null;
                    for (const company of companies) {
                        const premiumData = getPremiumForYear(company._id, year, selectedPersonIdNumber);
                        if (premiumData) {
                            displayAge = premiumData.age;
                            break;
                        }
                    }

                    // Skip years where age exceeds maxAge
                    if (displayAge !== null && displayAge > maxAge) {
                        return null;
                    }

                    return (
                        <tr key={year}>
                            <td>
                                <strong>{year}</strong>
                            </td>
                            {showAgeColumn && (
                                <td>
                                    <strong>{displayAge ?? '-'}</strong>
                                </td>
                            )}
                            {companies.map((company) => {
                                const isMin = minCompanyId === company._id;
                                const data = companyData.get(company._id);
                                const premiumData = getPremiumForYear(company._id, year, selectedPersonIdNumber);

                                return (
                                    <td key={company._id}>
                                        {data?.loading ? (
                                            <Spinner animation="border" size="sm" />
                                        ) : data?.error ? (
                                            <span className="text-danger">שגיאה</span>
                                        ) : premiumData ? (
                                            `₪${premiumData.premium.toFixed(0)}`
                                        ) : (
                                            '-'
                                        )}
                                    </td>
                                );
                            })}
                        </tr>
                    );
                })}
            </tbody>
        </Table>
    );

    return (
        <Modal show={show} onHide={onHide} dir="rtl" centered size="xl" onClick={(e: any) => e.stopPropagation()}>
            <Modal.Header className="bg-primary text-white" closeButton style={{ flexDirection: 'row-reverse' }}>
                <Modal.Title className="text-white">{t('Company Comparison')}</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {insuredPersons.length === 0 ? (
                    <Alert variant="warning">{t('Please select at least one insured person')}</Alert>
                ) : (
                    <>
                        {selectedCoverageTypes.length > 0 && (
                            <div className="mb-3">
                                <strong>{t('Coverages')}:</strong>{' '}
                                {selectedCoverageTypes.map((coverageType) => (
                                    <Badge key={coverageType} bg="info" className="me-1">
                                        {t(coverageType)}
                                    </Badge>
                                ))}
                            </div>
                        )}

                        {(loading || isAnyLoading) && (
                            <div className="text-center py-3">
                                <Spinner animation="border" role="status" variant="primary">
                                    <span className="visually-hidden">{t('Loading...')}</span>
                                </Spinner>
                                <p className="mt-2">{t('Loading data...')}</p>
                            </div>
                        )}

                        {allPersons.length > 1 ? (
                            <Tab.Container activeKey={selectedPersonTab} onSelect={(k) => setSelectedPersonTab(k || 'all')}>
                                <Nav variant="tabs" className="mb-3">
                                    <Nav.Item>
                                        <Nav.Link eventKey="all">{t('All Insured')}</Nav.Link>
                                    </Nav.Item>
                                    {allPersons.map((person) => (
                                        <Nav.Item key={person.idNumber}>
                                            <Nav.Link eventKey={person.idNumber}>{person.name}</Nav.Link>
                                        </Nav.Item>
                                    ))}
                                </Nav>
                                <Tab.Content>
                                    <Tab.Pane eventKey={selectedPersonTab}>{renderComparisonTable()}</Tab.Pane>
                                </Tab.Content>
                            </Tab.Container>
                        ) : (
                            renderComparisonTable()
                        )}

                        {allYears.length === 0 && !isAnyLoading && (
                            <Alert variant="info">{t('No premium data available')}</Alert>
                        )}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer style={{ justifyContent: 'flex-start' }}>
                <Button variant="secondary" onClick={onHide}>
                    {t('Close')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default CompanyComparisonModal;
